// =============================================================================
// domain/capture.ts — MFE Capture and Peak Capture.
// -----------------------------------------------------------------------------
// Relocated from the old client's `tradeMfeR` / `tradeMfeCapturePct` /
// `dashMfeCapture` / `_peakExposure` / `tradePeakCapturePct` / `dashPeakCapture`.
// Not redesigned: these two numbers sit side by side on the dashboard and the
// whole point of the pair is that they disagree in a specific, readable way.
//
// ── WHAT THE TWO ACTUALLY ASK ──
//
// **MFE Capture** grades the blended VWAP exit against the WHOLE-position peak:
// `realized points ÷ MFE points`. Simple, and unfair to a scale-out trader — if
// the peak prints AFTER two thirds of the position is already booked, that
// booked size gets graded against a high it never had a shot at.
//
// **Peak Capture** grades only the contracts that were STILL ON when the peak
// printed, each against the full MFE, quantity-weighted. It needs the per-fill
// timeline to know which those were, so it is available on strictly fewer
// trades — and a big gap ABOVE MFE Capture is the finding: early scale-outs are
// dragging the simpler number down, and the exit is better than it looks.
//
// ── ⚠ BOTH ARE MEDIANS, AND THAT IS NOT A STYLE CHOICE ──
//
// Capture has an unbounded NEGATIVE tail: a trade that ran a full R in your
// favour and then closed red has a negative capture with no floor. A mean gets
// dragged by two of those; the median answers "what does a typical trade do",
// which is the question the tile is asking. The old client's comment says the
// same and it is load-bearing on this book — see the ≥1R gate below.
//
// ── ⚠ THE ≥1R GATE ──
//
// Only trades whose MFE reached at least one full unit of risk are eligible.
// Trades that never offered a full R of reward are noise here: capturing 40% of
// a move that was never worth taking says nothing about exit skill. Mirrors the
// Risk-Adjusted Win Rate's gate, deliberately — the two tiles are read together.
//
// No `annotated` gate on either, which is also deliberate: the old client
// removed it so the dashboard tile and the Analytics card report the same
// sample. Imported trades count.
// =============================================================================

import { dollarsPerPointFor, type ContractSpec } from './trade';

// ---------------------------------------------------------------------------
// MFE Capture
// ---------------------------------------------------------------------------

/** The three fields the simple capture reads. Narrower than a full Trade. */
export interface CaptureTrade {
  points: number | null;
  mfePoints: number | null;
  riskPoints: number | null;
}

/**
 * How far the trade ran in your favour, in units of risk.
 *
 * ⚠ `!riskPoints` rather than `riskPoints == null` — a ZERO risk basis is not a
 * denominator, and treating it as one yields Infinity, which then passes a
 * `>= 1` gate and admits a trade with no stop into an R-gated population.
 */
export function tradeMfeR(t: CaptureTrade): number | null {
  const mfe = t.mfePoints;
  const rp = t.riskPoints;
  if (mfe == null || Number.isNaN(mfe) || !rp) return null;
  return mfe / rp;
}

/**
 * The share of the favourable excursion the trade actually banked.
 *
 * 100% = closed at the best print. Above 100% is rare but real (the excursion
 * feed and the fill disagree by a tick). NEGATIVE is the important case and is
 * allowed through: the trade ran in your favour and you still closed red.
 *
 * ⚠ `mfe === 0` returns null rather than dividing. A trade that never moved in
 * your favour has no capture to report, and 0/0 would render as NaN%.
 */
export function tradeMfeCapturePct(t: CaptureTrade): number | null {
  const mfe = t.mfePoints;
  const pts = t.points;
  if (mfe == null || Number.isNaN(mfe) || mfe === 0) return null;
  if (pts == null || Number.isNaN(pts)) return null;
  return (pts / mfe) * 100;
}

export interface CaptureStat {
  /** Median capture %, or null when nothing qualifies. */
  pct: number | null;
  /** How many trades are behind it. Rendered — see the note in Dashboard.tsx. */
  n: number;
}

/** Median of a non-empty numeric list. Sorts a COPY — callers keep their order. */
function median(xs: readonly number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  const i = s.length >> 1;
  const hi = s[i];
  const lo = s[i - 1];
  // ⚠ The two `undefined` branches are unreachable — every caller checks the
  // length first — and are written out rather than asserted away because
  // `noUncheckedIndexedAccess` is on for a reason and a `!` here would be the
  // one place it stopped applying.
  if (hi === undefined) return 0;
  return s.length % 2 || lo === undefined ? hi : (lo + hi) / 2;
}

export function mfeCapture(trades: readonly CaptureTrade[]): CaptureStat {
  const caps: number[] = [];
  for (const t of trades) {
    const r = tradeMfeR(t);
    if (r == null || r < 1) continue;
    const c = tradeMfeCapturePct(t);
    if (c != null) caps.push(c);
  }
  return caps.length ? { pct: median(caps), n: caps.length } : { pct: null, n: 0 };
}

// ---------------------------------------------------------------------------
// Peak Capture — the FIFO reconstruction
// ---------------------------------------------------------------------------

/**
 * One fill. The shape both the realtime logger push and the importer walk
 * normalise to, and the shape stored in `extras.executions`.
 */
export interface Execution {
  time: string | null;
  side: string | null;
  qty: number | null;
  price: number | null;
}

export interface PeakTrade extends CaptureTrade {
  contract: string | null;
  side: string | null;
  entry: number | null;
  timeIn: string | null;
  /** Seconds from ENTRY to the favourable peak — relative, not clock time. */
  mfeSecs: number | null;
  executions?: readonly Execution[] | null;
}

/**
 * `"HH:MM[:SS]"` → seconds since midnight. null when unparseable.
 *
 * ⚠ Validates the components rather than just the shape. `"25:00:00"` matches
 * the digits and is not a time; letting it through would place a fill outside
 * the day and silently reorder the timeline.
 */
export function secOfClock(s: string | null | undefined): number | null {
  if (typeof s !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(s);
  // ⚠ The two group checks are unreachable after a match and are here rather
  // than as `!` assertions: `+undefined` is NaN, and `NaN > 23` is FALSE, so an
  // asserted-away group would sail through the range check below and return NaN
  // seconds — a fill with no place on the timeline that still looks like one.
  if (!m || m[1] === undefined || m[2] === undefined) return null;
  const h = +m[1], mi = +m[2], se = m[3] === undefined ? 0 : +m[3];
  if (h > 23 || mi > 59 || se > 59) return null;
  return h * 3600 + mi * 60 + se;
}

export interface PeakExposure {
  /** Contracts that were open when the peak printed. */
  sizeAtPeak: number;
  /** Points those contracts realised against the entry VWAP, qty-weighted. */
  realizedPtsOnPeak: number;
  /** Dollars handed back from the peak by that size. Feeds Cost of Give-Back. */
  giveBackUsd: number;
  /** Absolute MFE in points, echoed so callers need not re-read it. */
  mfePoints: number;
}

/**
 * Walk the per-fill timeline and reconstruct the position AT THE PEAK.
 *
 * A contract was on at the peak iff it opened at-or-before the peak and had not
 * closed yet when the peak happened: `entrySec <= peakSec < exitSec`.
 *
 * ── 🚨 RETURNS NULL RATHER THAN GUESSING, IN SEVEN PLACES ──
 *
 * No executions, fewer than two of them, an unparseable fill clock, a missing
 * `mfeSecs`, a side that is neither long nor short, a close that consumes more
 * than is open, or a timeline that never comes back flat. Every one of those is
 * "this trade's fills do not reconcile", and the honest answer is to drop the
 * trade out of the aggregate — a partial reconstruction would produce a
 * confident number from an incomplete timeline, which is worse than a smaller
 * sample. The caller renders `no fill timeline`.
 *
 * ⚠ `dollarsPerPoint` gates the whole function even though only `giveBackUsd`
 * spends it. That is the old client's behaviour (`dppForTrade` returns 0 for an
 * uncatalogued root and `_peakExposure` bails), so an uncatalogued instrument
 * drops out of Peak Capture even though the RATIO would not have needed a
 * dollar value. Kept, because the two metrics built on this reconstruction have
 * to describe the same set of trades or they stop telling one story.
 */
export function peakExposure(
  t: PeakTrade,
  dollarsPerPoint: number,
): PeakExposure | null {
  const { mfeSecs, mfePoints, entry, side, timeIn } = t;
  if (mfeSecs == null || Number.isNaN(mfeSecs)) return null;
  if (mfePoints == null || Number.isNaN(mfePoints)) return null;
  if (entry == null || Number.isNaN(entry)) return null;
  if (side !== 'long' && side !== 'short') return null;

  const t0 = secOfClock(timeIn);
  if (t0 == null) return null;
  if (!(dollarsPerPoint > 0)) return null;

  const raw = Array.isArray(t.executions) ? t.executions : null;
  if (!raw || raw.length < 2) return null;

  const fills: { sec: number; qty: number; isBuy: boolean; price: number; i: number }[] = [];
  for (let i = 0; i < raw.length; i++) {
    const e = raw[i];
    if (!e) return null;
    const qty = Math.abs(Number(e.qty));
    if (!(qty > 0)) return null;
    const price = Number(e.price);
    if (!Number.isFinite(price)) return null;

    const sideStr = String(e.side ?? '').toLowerCase();
    let isBuy: boolean;
    if (sideStr === 'buy' || sideStr === 'b' || sideStr === 'long') isBuy = true;
    else if (sideStr === 'sell' || sideStr === 's' || sideStr === 'short') isBuy = false;
    else return null;

    const secAbs = secOfClock(e.time);
    if (secAbs == null) return null;
    let sec = secAbs - t0;
    // ⚠ A fill clock BEFORE the entry clock means the session rolled past
    // midnight, not that the fill preceded the entry. Without this an overnight
    // trade's exits sort ahead of its entry and the walk fails to reconcile.
    if (sec < 0) sec += 86400;
    fills.push({ sec, qty, isBuy, price, i });
  }
  // ⚠ Stable: ties keep their original array order, because two fills stamped
  // the same second are ordered by the feed, not by anything derivable here.
  fills.sort((a, b) => (a.sec - b.sec) || (a.i - b.i));

  const abs = Math.abs(mfePoints);
  const peakPrice = side === 'long' ? entry + abs : entry - abs;
  const peakSec = mfeSecs;
  const opensLong = side === 'long';

  const queue: { sec: number; qty: number }[] = [];
  let giveBackUsd = 0, sizeAtPeak = 0, realizedPtsOnPeak = 0;

  for (const f of fills) {
    if (f.isBuy === opensLong) { queue.push({ sec: f.sec, qty: f.qty }); continue; }

    let remaining = f.qty;
    while (remaining > 1e-9) {
      const lot = queue[0];
      if (!lot) return null;   // closes more than is open — does not reconcile
      const closeQty = Math.min(lot.qty, remaining);
      if (lot.sec <= peakSec && peakSec < f.sec) {
        const gb = side === 'long' ? (peakPrice - f.price) : (f.price - peakPrice);
        // ⚠ Floored at zero: a lot that exited BEYOND the peak gave nothing
        // back. Letting it go negative would net off against a genuine
        // give-back elsewhere in the same trade and understate the leak.
        giveBackUsd += Math.max(0, gb) * dollarsPerPoint * closeQty;
        sizeAtPeak += closeQty;
        realizedPtsOnPeak += (side === 'long' ? (f.price - entry) : (entry - f.price)) * closeQty;
      }
      lot.qty -= closeQty;
      remaining -= closeQty;
      if (lot.qty <= 1e-9) queue.shift();
    }
  }
  if (queue.length) return null;   // never came back flat — does not reconcile

  return { sizeAtPeak, realizedPtsOnPeak, giveBackUsd, mfePoints: abs };
}

/**
 * Size-at-peak capture for ONE trade.
 *
 * The denominator is `mfePoints × sizeAtPeak` — the full favourable move, once
 * per contract that was actually exposed to it. Per-lot capture can go negative
 * for the same reason the simple one can: those contracts rode the peak and
 * then closed red.
 */
export function tradePeakCapturePct(
  t: PeakTrade,
  dollarsPerPoint: number,
): number | null {
  const e = peakExposure(t, dollarsPerPoint);
  if (!e || !(e.sizeAtPeak > 0) || !(e.mfePoints > 0)) return null;
  return (e.realizedPtsOnPeak / (e.mfePoints * e.sizeAtPeak)) * 100;
}

/**
 * Median size-at-peak capture.
 *
 * ⚠ The SAME ≥1R gate as `mfeCapture`, so the two tiles describe the same
 * eligible population minus the trades whose fills cannot reconstruct. Without
 * that the pair would be uncomparable and the gap between them — which is the
 * entire reason both are on screen — would mean nothing.
 */
export function peakCapture(
  trades: readonly PeakTrade[],
  catalogue: readonly ContractSpec[],
): CaptureStat {
  const caps: number[] = [];
  for (const t of trades) {
    const r = tradeMfeR(t);
    if (r == null || r < 1) continue;
    const p = tradePeakCapturePct(t, dollarsPerPointFor(t.contract, catalogue));
    if (p != null) caps.push(p);
  }
  return caps.length ? { pct: median(caps), n: caps.length } : { pct: null, n: 0 };
}
